import { Link } from 'react-router-dom';
import { useAdminDisputes } from '../../hooks/useAdminDisputes';
import { Spinner, Button } from '../../components/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';

const DISPUTE_REASONS = {
  notAsDescribed: 'Item not as described',
  notReceived: 'Item not received / winner no-show',
  sellerUnresponsive: 'Seller is unresponsive',
  winnerUnresponsive: 'Winner is unresponsive',
  damaged: 'Item was damaged',
};

const RESOLUTION_LABELS = {
  buyerFavour: 'Winner\'s favour',
  sellerFavour: 'Seller\'s favour',
  split: 'Split',
};

const STATUS_COLORS = {
  open: 'bg-accent text-midnight',
  underReview: 'bg-primary text-midnight',
  resolved: 'bg-success text-midnight',
};

function formatDate(date) {
  if (!date) return 'N/A';
  return new Intl.DateTimeFormat('en-IE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function DisputeRow({ dispute }) {
  return (
    <Link
      to={`/admin/disputes/${dispute.id}`}
      className="block bg-midnight-light rounded-lg p-4 border border-cream/10 hover:border-primary/30 transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                STATUS_COLORS[dispute.status] || 'bg-cream/20 text-cream'
              }`}
            >
              {dispute.status === 'underReview' ? 'Under Review' : dispute.status}
            </span>
            <span className="text-cream/50 text-xs">
              {formatDate(dispute.createdAt)}
            </span>
          </div>

          <h3 className="text-cream font-medium truncate mb-1">
            {dispute.raffleTitle}
          </h3>

          <p className="text-cream/60 text-sm">
            {DISPUTE_REASONS[dispute.reason] || dispute.reason}
          </p>

          {dispute.status === 'resolved' && dispute.resolution && (
            <p className="text-success text-xs mt-2">
              Resolved: {RESOLUTION_LABELS[dispute.resolution] || dispute.resolution}
            </p>
          )}
        </div>

        <ChevronIcon className="w-5 h-5 text-cream/40 shrink-0" />
      </div>
    </Link>
  );
}

function DisputesList({ disputes, emptyMessage }) {
  if (disputes.length === 0) {
    return (
      <div className="text-center py-12 text-cream/50">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {disputes.map((dispute) => (
        <DisputeRow key={dispute.id} dispute={dispute} />
      ))}
    </div>
  );
}

export default function DisputeList() {
  const {
    disputes,
    openDisputes,
    underReviewDisputes,
    resolvedDisputes,
    loading,
    error,
  } = useAdminDisputes();

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-midnight">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-display font-bold text-cream mb-4">
            Error Loading Disputes
          </h1>
          <p className="text-cream/60 mb-8">{error}</p>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold text-cream mb-2">
            Dispute Management
          </h1>
          <p className="text-cream/60">
            Review and resolve user disputes
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-accent">{openDisputes.length}</p>
            <p className="text-cream/60 text-sm">Open</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-primary">{underReviewDisputes.length}</p>
            <p className="text-cream/60 text-sm">Under Review</p>
          </div>
          <div className="bg-success/10 border border-success/30 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-success">{resolvedDisputes.length}</p>
            <p className="text-cream/60 text-sm">Resolved</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="open">
          <TabsList className="mb-6">
            <TabsTrigger value="open">
              Open ({openDisputes.length})
            </TabsTrigger>
            <TabsTrigger value="underReview">
              Under Review ({underReviewDisputes.length})
            </TabsTrigger>
            <TabsTrigger value="resolved">
              Resolved ({resolvedDisputes.length})
            </TabsTrigger>
            <TabsTrigger value="all">
              All ({disputes.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="open">
            <DisputesList
              disputes={openDisputes}
              emptyMessage="No open disputes"
            />
          </TabsContent>

          <TabsContent value="underReview">
            <DisputesList
              disputes={underReviewDisputes}
              emptyMessage="No disputes under review"
            />
          </TabsContent>

          <TabsContent value="resolved">
            <DisputesList
              disputes={resolvedDisputes}
              emptyMessage="No resolved disputes"
            />
          </TabsContent>

          <TabsContent value="all">
            <DisputesList
              disputes={disputes}
              emptyMessage="No disputes found"
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ChevronIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
